/**
 * Measured-facing promotion (ADR-004 geometric evidence → the convention the
 * retarget consumes; ADR-008 §2c's "corrected by ADR-004 geometric evidence
 * downstream", discharged). The adapter declares what the format asserts; a
 * rig that LIES about facing (the Tripo corpus discovery: sides spread along
 * the declared facing axis) poisons every declared-convention consumer —
 * conventionRotation aims root motion 90° off (the sideways moonwalk), the
 * canonical-secondary twist tier transports anatomy into the wrong axis, and
 * the reflection scan searches the wrong sagittal plane. Promotion replaces
 * the declaration with the MEASURED facing, with provenance, and re-runs the
 * reflection scan under the corrected convention so `reflectedPairs` keeps
 * its "detection ran under this descriptor's conventions" invariant.
 * Never silent: the returned promotion record is the loud form (§11-style
 * correctable — the record is what a review UI offers to override).
 *
 * KNOWN BOUNDARY (Codex, PR #21): a facing-liar whose bind frames are ALSO
 * reflection-related never reaches this function — convertRig's scan runs
 * under the DECLARED (lying) plane and throws ImproperBindFrameError first
 * (ADR-008 §7's conversion gate). That failure is loud and typed, never a
 * silent mangle; teaching the conversion gate to defer to post-mapping
 * evidence is a pipeline-ordering decision that waits for a real specimen
 * of that class (same doctrine as the Z-up FBX refusal).
 * Depends on: ir/rig-descriptor, mapping/reflection, map-rig (report type).
 */
import { parseRigDescriptor, type RigDescriptor } from '../ir/rig-descriptor.js';
import { KernelError } from '../ir/errors.js';
import { detectReflectedPairs } from './reflection.js';
import type { MappingReport } from './map-rig.js';

/**
 * A promotion may only widen what the scan can pair — but an improper bind
 * frame that was declarable under the old convention MUST stay declarable
 * under the corrected one, or the descriptor would regress from "reflection
 * declared" to "reflection undeclared" while looking cleaner (ADR-005 §7).
 */
export class FacingPromotionStrandsReflectionError extends KernelError {
  constructor(
    readonly label: string,
    readonly strandedBones: readonly string[],
  ) {
    super(
      `Promoting the measured facing of "${label}" would strand ${strandedBones.length} ` +
        `improper bind frame(s) outside any declarable mirror pair: ${strandedBones.join(', ')}. ` +
        `Refusing the promotion — the reflection contract outranks the facing correction.`,
    );
  }
}

export interface FacingPromotion {
  readonly from: RigDescriptor['facing'];
  readonly to: RigDescriptor['facing'];
  readonly provenance: 'measured:mapping-geometry';
  /** The reflection scan re-run under the corrected convention. */
  readonly rescan: {
    readonly mirrorPairs: number;
    readonly unpairedSided: readonly string[];
    readonly reflectedPairs: number;
  };
}

export interface PromoteFacingResult {
  /** The rig to use downstream — corrected when promotion fired, the input otherwise. */
  readonly rig: RigDescriptor;
  /** Null when the evidence agrees with the declaration or is indeterminate. */
  readonly promotion: FacingPromotion | null;
}

export function promoteMeasuredFacing(rig: RigDescriptor, report: MappingReport): PromoteFacingResult {
  const measured = report.convention.measuredFacing;
  if (measured === null || measured === rig.facing) return { rig, promotion: null };

  const scan = detectReflectedPairs(rig.bones, { upAxis: rig.upAxis, facing: measured });
  const declared = new Set(scan.reflectedPairs.flat());
  const stranded = scan.improperBones.filter((name) => !declared.has(name));
  if (stranded.length > 0) throw new FacingPromotionStrandsReflectionError(rig.label, stranded);

  const promoted = parseRigDescriptor({
    ...rig,
    facing: measured,
    reflectedPairs: scan.reflectedPairs.map(([a, b]) => [a, b]),
  });
  return {
    rig: promoted,
    promotion: {
      from: rig.facing,
      to: measured,
      provenance: 'measured:mapping-geometry',
      rescan: {
        mirrorPairs: scan.mirrorPairs.length,
        unpairedSided: scan.unpairedSided,
        reflectedPairs: scan.reflectedPairs.length,
      },
    },
  };
}
